import {
    Autocomplete, Box, Checkbox, Chip, FormControlLabel,
    IconButton,
    Input,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {useTheme} from '@mui/styles';
import {IconCurrencyShekel} from '@tabler/icons';
import {FormattedMessage} from 'react-intl';
import IItem, {
    chargedPositiveQuantity,
    hasSecondUnit,
    hasStoredSecondAmount,
    hasThirdUnit,
    orderColumnDisplay,
    secondUnitIndex,
    showsWrappedColumn,
    thirdUnitIndex,
    wrappedColumnDisplay,
    wrappedNetSecond
} from 'types/product_delivery';
import HoverableIncrementer from 'ui-component/HoverableIncrementer';
import JSDB from '../../../../fromKotlin/nk';
import {dateObjectToString, roundDecimals} from '../../../../fromKotlin/nkutils';

const roundQty = (value: number) => roundDecimals(value ?? 0, 3);
/** Multiply caption under the wrapped amount: at most 3 digits after the decimal. */
const formatMultiply = (value: number) => roundDecimals(value ?? 0, 3).toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
import React, {memo, useLayoutEffect, useRef} from 'react';
import getCustomerProductsPrices, {getSupplierProductsPrices, itemApiIInternal} from '../../../../api/customer/products_prices.api';
import {
    applyTaklitPriceFromLookup,
    isTaklitEnabled,
    loadTaklitLookup,
} from '../../../../api/customer/taklitPrices';
import {TaklitProduct} from '../../../../api/customer/taklit.api';
import {handleKeyDown} from '../../../../utils/tableAsKeyBorad';
import {toArrayOrEmpty} from '../../../../types/DocumentsTypes/utils';
import {ExtendedDocument} from "../../../../types/DocumentsTypes/Document";
import {subStringMatch} from "../../../../utils/sort";
import {Percentage} from "tabler-icons-react";
import {CUSTOMER_PRODUCTS, SUPPLIER_PRODUCTS} from "../../../../hooks/customer/products.hooks";
import {useDispatch} from "react-redux";
import {useQuery, useQueryClient} from "react-query";
import {SNACKBAR_OPEN} from "../../../../store/actions";
import {isVariableConversionRate} from "../../../../utils/cofcFeatures";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';

interface CustomerProductsPreviewListProps {
    products: IItem[];
    onProductChange?: (product: IItem, rowIndex?: number) => void;
    disableHoverableIncrementer?: boolean;
    withReturns?: boolean;
    date?: Date;
    id: string;
    sup?: boolean
    displayCost?: boolean;
    oldProducts?: IItem[] | undefined;
    displayOldProducts?: boolean;
    orderProducts?: IItem[] | undefined;
    displayOrderProducts?: boolean;
    collect?: boolean;
    jumpToWrapped?: boolean;
    /** Quote editor: Enter moves to the next product row. */
    enterMovesDown?: boolean;
    /** When set, the product column title is a toggle for catalog-position order. */
    sortByPosition?: boolean;
    onTogglePositionSort?: () => void;
}

const ProductRowMemo = React.memo(({
                                       row,
                                       onProductChange,
                                       i,
                                       disableHoverableIncrementer,
                                       withReturns,
                                       withExtraUnit,
                                       displayCost,
                                       displayNoteColumn,
                                       displayQuantityColumn,
                                       displayCostPrice,
                                       oldProducts,
                                       removedProduct,
                                       existingOrderProducts,
                                       orderProducts,
                                          withDiscount,
                                       externalIdSearch,
                                       jumpToWrapped,
                                       enterMovesDown,
                                       variableConversion,
                                       onDuplicateLines,

                                   }: { row: IItem, onProductChange?: any, i: number, disableHoverableIncrementer: any, withReturns: boolean, withExtraUnit: boolean, displayCost?: boolean, displayNoteColumn?: boolean, displayQuantityColumn?: boolean, displayCostPrice?: boolean, oldProducts?: IItem[], removedProduct?: boolean, existingOrderProducts?: IItem[], orderProducts?: boolean, collect?: boolean,withDiscount?:boolean ,externalIdSearch?:boolean,jumpToWrapped?:boolean, enterMovesDown?: boolean, variableConversion?: boolean, onDuplicateLines?: (row: IItem, count: number) => void}) => {
    const oldQuantity = !oldProducts ? undefined : (removedProduct ? null : oldProducts?.find(item => item?.id === row?.id));
    const orderP = existingOrderProducts?.length || orderProducts ? (orderProducts ? row : existingOrderProducts?.find(item => item?.id === row?.id)) : undefined
    const secondIdx = secondUnitIndex(row);
    const thirdIdx = thirdUnitIndex(row);
    const secondUnitName = secondIdx >= 0 ? row.available_units[secondIdx] : undefined;
    const thirdUnitName = thirdIdx >= 0 ? row.available_units[thirdIdx] : undefined;
    const leftoverSecond = hasStoredSecondAmount(row);
    const showSecondAmount = hasSecondUnit(row) || leftoverSecond;
    const showWrappedCell = showsWrappedColumn(row);
    const positiveQty = chargedPositiveQuantity(row);
    const [copyAnchor, setCopyAnchor] = React.useState<HTMLElement | null>(null);
    const [copyCount, setCopyCount] = React.useState('10');
    const canCopyLines = !!variableConversion && !!onDuplicateLines && !!onProductChange && !orderProducts && !removedProduct;

    return <TableRow
        key={i}
        data-line={row.notePlacement != null ? String(row.notePlacement) : undefined}
        data-extra={hasSecondUnit(row) || hasThirdUnit(row) ? '1' : undefined}
        onKeyDown={(e) => handleKeyDown(e, { enterMovesDown })}
        sx={{
            '&:last-child td, &:last-child th': {border: 0},
            backgroundColor: row.isLocked ? '#95bdf6' : (removedProduct ? '#ffd1d1' : orderProducts ? "#cfeeff" : undefined)
        }}
    >
        <TableCell key='name' align='left' sx={{padding: 0}} style={{textAlign: 'right', padding: 0}}>
            <div style={{display: 'flex', flexDirection: 'row', direction: 'ltr', justifyContent: 'flex-end', alignItems: 'center', width: '100%', gap: 4}}>
                <div style={{textAlign: 'right'}}>
                    <Typography variant='h5' style={{textAlign: 'right'}}>      {externalIdSearch? row.external_id : row.id}  - {row.name}</Typography>
                    <Typography variant='caption' style={{textAlign: 'right', display: 'block'}}> {row.barcode}</Typography>
                </div>
                {canCopyLines ? (
                    <IconButton
                        size='small'
                        title='שורות זהות'
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCopyCount('10');
                            setCopyAnchor(e.currentTarget);
                        }}
                        sx={{p: 0.25, flexShrink: 0}}
                    >
                        <LibraryAddOutlinedIcon fontSize='small'/>
                    </IconButton>
                ) : null}
            </div>
            <Popover
                open={Boolean(copyAnchor)}
                anchorEl={copyAnchor}
                onClose={() => setCopyAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                style={{zIndex: 2600}}
                sx={{zIndex: 2600}}
                PaperProps={{sx: {p: 1, display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'visible'}}}
            >
                <TextField
                    autoFocus
                    size='small'
                    type='number'
                    label='כמה שורות'
                    value={copyCount}
                    onChange={(e) => setCopyCount(e.target.value)}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key !== 'Enter') return;
                        const n = Math.floor(Number(copyCount));
                        if (n > 0) onDuplicateLines?.(row, n);
                        setCopyAnchor(null);
                    }}
                    inputProps={{min: 1, style: {width: 72}}}
                />
                <Chip
                    size='small'
                    color='primary'
                    label='הוסף'
                    onClick={() => {
                        const n = Math.floor(Number(copyCount));
                        if (n > 0) onDuplicateLines?.(row, n);
                        setCopyAnchor(null);
                    }}
                />
            </Popover>
        </TableCell>
        {existingOrderProducts?.length || orderProducts ? (
            <TableCell key='order' sx={{padding: 0}} align='center'>
                <Typography variant='h5' color={"#0288d1"}
                            fontWeight={"600"} sx={{whiteSpace: 'pre-line'}}>
                    {orderP ? orderColumnDisplay(orderP) : 0}
                </Typography>
            </TableCell>
        ) : undefined
        }
        {withExtraUnit ? showWrappedCell ?
            <TableCell key='wrapped' sx={{padding: 0}} align='left'>

                {!onProductChange ? (
                    <Typography variant='h5' sx={{whiteSpace: 'pre-line'}}>
                        {!orderProducts && oldQuantity && oldQuantity.quantitySecond !== undefined && oldQuantity.quantitySecond !== row.quantitySecond ?
                            <span style={{color: 'red'}}>{roundQty(oldQuantity.quantitySecond)}  &nbsp; </span>
                            : undefined}
                        {wrappedColumnDisplay(row)}
                    </Typography>
                ) : (<>
                        {hasSecondUnit(row) && !jumpToWrapped && wrappedNetSecond(row) === 0 ? (
                            <Chip label={`${secondUnitName} +`} chipcolor={'success'} size='small'
                                  onClick={(e) => {
                                      onProductChange({
                                          ...row,
                                          quantitySecond: row.quantitySecond + 1
                                      });
                                  }}/>
                        ) : showSecondAmount ? (
                            <div data-nav='qty2'><HoverableIncrementer
                                key='qty2'
                                value={row.quantitySecond}
                                disabled={disableHoverableIncrementer}
                                onIncrement={() => {
                                    onProductChange({
                                        ...row,
                                        quantitySecond: row.quantitySecond + 1
                                    });
                                }}
                                onDecrement={() => {
                                    onProductChange({
                                        ...row,
                                        quantitySecond: Math.max(0, row.quantitySecond - 1)
                                    });
                                }}
                                onChange={(v) => {
                                    onProductChange({...row, quantitySecond: Math.max(0, v)});
                                }}
                            />
                                {secondUnitName ?
                                    <Typography variant='h5'>    &nbsp; {secondUnitName}   </Typography> : undefined}
                            </div>
                        ) : undefined}

                        {variableConversion && showSecondAmount && (row.quantitySecond ?? 0) !== 0 && (row.conversion_ratio ?? 0) > 0 && !orderProducts ?
                            `${formatMultiply((row.quantitySecond ?? 0) * (row.conversion_ratio ?? 0))} ${row?.available_units[0] ?? ''}`
                            : hasSecondUnit(row) && row?.conversion_ratio < 1 && row?.conversion_ratio > 0 ? ` = ${formatMultiply(1 / row?.conversion_ratio)} ${row?.available_units[0]}` : showSecondAmount && row.quantitySecond > 0 && row.conversion_ratio > 0 && !orderProducts ?
                            `${formatMultiply(row.quantitySecond * row.conversion_ratio)} ${row?.available_units[0]}`
                            : undefined}

                        {hasThirdUnit(row) ? (
                            <div data-nav='qty3' style={{display: "flex", flexDirection: 'row'}}>
                                <HoverableIncrementer
                                    key='qty3'
                                    value={row.quantityThird ?? 0}
                                    disabled={disableHoverableIncrementer}
                                    onIncrement={() => {
                                        onProductChange({...row, quantityThird: (row.quantityThird ?? 0) + 1});
                                    }}
                                    onDecrement={() => {
                                        onProductChange({...row, quantityThird: Math.max(0, (row.quantityThird ?? 0) - 1)});
                                    }}
                                    onChange={(v) => {
                                        onProductChange({...row, quantityThird: Math.max(0, v)});
                                    }}
                                />
                                <Typography variant='h5'>    &nbsp; {thirdUnitName}   </Typography>
                            </div>
                        ) : undefined}
                    </>
                )}
            </TableCell> : <TableCell key='wrapped' sx={{padding: 0}} align='left'>
                <Typography variant='h5'> </Typography>
            </TableCell> : undefined}

        {variableConversion && withExtraUnit ? (
            <TableCell key='conv' data-nav={onProductChange && hasSecondUnit(row) ? 'conv' : undefined} sx={{py: 0, px: 0.5, whiteSpace: 'nowrap'}} align='center'>
                {hasSecondUnit(row) ? (
                    !onProductChange ? (
                        <Typography variant='h5'>{row.conversion_ratio ? roundQty(row.conversion_ratio) : ''}</Typography>
                    ) : (
                        <HoverableIncrementer
                            key='conv'
                            value={row.conversion_ratio ?? 0}
                            disabled={disableHoverableIncrementer}
                            onIncrement={() => {
                                onProductChange({...row, conversion_ratio: (row.conversion_ratio ?? 0) + 1});
                            }}
                            onDecrement={() => {
                                onProductChange({...row, conversion_ratio: Math.max(0, (row.conversion_ratio ?? 0) - 1)});
                            }}
                            onChange={(v) => {
                                onProductChange({...row, conversion_ratio: Math.max(0, v)});
                            }}
                        />
                    )
                ) : null}
            </TableCell>
        ) : undefined}

        <TableCell key='qty' data-nav={onProductChange ? 'qty' : undefined} sx={{py: 0, px: 0.5, whiteSpace: 'nowrap'}} align='center'>
            {!onProductChange ? (
                <div>
                    {!orderProducts ? <>
                        {(() => {
                            const oldCombined = oldQuantity ? roundQty(chargedPositiveQuantity(oldQuantity)) : undefined;
                            const currentCombined = roundQty(positiveQty);
                            const changed = oldCombined !== undefined && oldCombined !== currentCombined;
                            return <>
                                {changed ? <Typography variant='h5' style={{ color: 'red' }}>{oldCombined}</Typography> : undefined}
                                <Typography variant='h5' style={{ margin: "0 5px" }}>{currentCombined}</Typography>
                            </>;
                        })()}

                        {row.in_unit && row.in_unit > 0 && row.quantity > 0 ?
                            <Typography
                                variant='caption'>  {`${roundQty(row.quantity * row.in_unit)} יח'`}</Typography> : undefined}
                    </> : undefined}


                </div>
            ) : (
                <HoverableIncrementer
                    key='qty'
                    value={row.quantity}
                    disabled={disableHoverableIncrementer}
                    onIncrement={() => {
                        onProductChange({...row, quantity: row.quantity + 1});
                    }}
                    onDecrement={() => {
                        onProductChange({...row, quantity: Math.max(0, row.quantity - 1)});
                    }}
                    onChange={(v) => {
                        onProductChange({...row, quantity: Math.max(0, v)});
                    }}
                />
            )}
        </TableCell>

        {withReturns && (displayQuantityColumn || onProductChange) ? <TableCell key='returns' data-nav={onProductChange ? 'returns' : undefined} sx={{py: 0, px: 0.5, whiteSpace: 'nowrap'}} align='center'>
            {!onProductChange ? (
                <div>
                    {!orderProducts ? <>
                        {(oldQuantity && oldQuantity.quantityReturns !== undefined && oldQuantity.quantityReturns !== row.quantityReturns) ?
                            <Typography variant="h5" style={{
                                color: 'red',
                            }}>{roundQty(oldQuantity.quantityReturns)}   </Typography>
                            : undefined
                        }
                        <Typography variant='h5' style={{
                            margin: "0 5px"
                        }}>{roundQty(row.quantityReturns)}</Typography>
                    </> : undefined}

                </div>


            ) : (
                <HoverableIncrementer
                    key='returns'
                    value={row.quantityReturns}
                    disabled={disableHoverableIncrementer}
                    onIncrement={() => {
                        onProductChange({
                            ...row,
                            quantityReturns: row.quantityReturns + 1
                        });
                    }}
                    onDecrement={() => {
                        onProductChange({
                            ...row,
                            quantityReturns: Math.max(0, row.quantityReturns - 1)
                        });
                    }}
                    onChange={(v) => {
                        onProductChange({...row, quantityReturns: Math.max(0, v)});
                    }}
                />
            )}
        </TableCell> : undefined}

        <TableCell key='price' data-nav={onProductChange ? 'price' : undefined} sx={{py: 0, px: 0.5, whiteSpace: 'nowrap'}} align='center'>
            {!onProductChange ? (
                <> 
                    <span style={{color: (oldQuantity && oldQuantity.totalPrice !== undefined && oldQuantity.totalPrice !== row.totalPrice) ? 'red' : undefined}}>
                    {row.price_min && row.price_min!=row.price_max ?  `${row.price_min} - ${row.price_max}`: roundDecimals(row.totalPrice, 4)}
                    </span>
                    <IconCurrencyShekel size={10} color='black'/>
                </>
            ) : (
                <HoverableIncrementer
                    key='price'
                    value={roundDecimals(row.totalPrice, 4)}
                    disabled={disableHoverableIncrementer}
                    wide
                    onIncrement={() => {
                        onProductChange({
                            ...row,
                            totalPrice: row.totalPrice + 1
                        });
                    }}
                    onDecrement={() => {
                        onProductChange({
                            ...row,
                            totalPrice: Math.max(0, row.totalPrice - 1)
                        });
                    }}
                    onChange={(v) => {
                        onProductChange({...row, totalPrice: Math.max(0, v)});
                    }}
                />
            )}
            {row.discount > 0 ?
                <Typography variant='caption' display='block' dir='ltr'
                            sx={{whiteSpace: 'nowrap', color: 'text.secondary'}}>
                    {`(${row.discount}%) => ${roundDecimals(row.totalPrice * (1 - row.discount / 100), 4)}`}
                </Typography> : ''}
        </TableCell>
        {withDiscount ?      <TableCell key='discount' data-nav={onProductChange ? 'discount' : undefined} sx={{py: 0, px: 0.5, whiteSpace: 'nowrap'}} align='center'>
            {!onProductChange ? (
                <>
                    {roundDecimals(row.discount, 4)}
                    <Percentage size={10} color='black'/>
                </>
            ) : (
                <HoverableIncrementer
                    key='discount'
                    value={roundDecimals(row.discount, 4)}
                    disabled={disableHoverableIncrementer}
                    onIncrement={() => {
                        onProductChange({
                            ...row,
                            discount: Math.min(row.discount + 1,100)
                        });
                    }}
                    onDecrement={() => {
                        onProductChange({
                            ...row,
                            discount: Math.max(0, row.discount - 1)
                        });
                    }}
                    onChange={(v) => {
                        onProductChange({...row, discount: Math.min(Math.max(0, v),100)});
                    }}
                />
            )}
        </TableCell>
            : undefined}
        <TableCell key='total' dir='ltr' sx={{py: 0, px: 0.5, whiteSpace: 'nowrap'}} align='center'>
            {(() => {
                const currentQty = roundQty(positiveQty - (row.quantityReturns ?? 0));
                const currentTotal = row.total ? row.total : roundDecimals((row.totalPrice * (1 - row.discount / 100)) * currentQty, 4);
                const oldPositive = oldQuantity ? chargedPositiveQuantity(oldQuantity) : 0;
                const oldTotal = oldQuantity ? roundDecimals(((oldQuantity.totalPrice ?? row.totalPrice) * (1 - (oldQuantity.discount ?? 0) / 100)) * roundQty(oldPositive - (oldQuantity.quantityReturns ?? 0)), 4) : undefined;
                const changed = oldTotal !== undefined && oldTotal !== currentTotal;
                return <>

                    <span>{currentTotal}</span>
                </>;
            })()}
            <IconCurrencyShekel size={10} color='black'/>
        </TableCell>
        {displayCost && displayCostPrice ?
            <TableCell key='cost' data-nav={onProductChange ? 'cost' : undefined} dir='ltr' sx={{py: 0, px: 0.5, whiteSpace: 'nowrap', backgroundColor: removedProduct ? '#ffadad' : '#F6F6F6'}}
                       align='center'>
                {
                    !onProductChange ? (
                        <>
                            {row.costPrice ? roundDecimals(row.costPrice, 4) : ""}
                            {row.costPrice ? <IconCurrencyShekel size={10} color='black'/> : ""}
                        </>
                    ) : (
                        <HoverableIncrementer
                            key='cost'
                            value={row.costPrice ? roundDecimals(row.costPrice, 4) : 0}
                            disabled={disableHoverableIncrementer}
                            wide
                            onIncrement={() => {
                                onProductChange({
                                    ...row,
                                    costPrice: row.costPrice ? row.costPrice + 1 : 1
                                });
                            }}
                            onDecrement={() => {
                                onProductChange({
                                    ...row,
                                    costPrice: Math.max(0, row.costPrice ? row.costPrice - 1 : 0)
                                });
                            }}
                            onChange={(v) => {
                                onProductChange({...row, costPrice: Math.max(0, v)});
                            }}
                        />
                    )
                }
            </TableCell>
            : undefined
        }
        {displayNoteColumn || onProductChange ?
            <TableCell key='notes' data-nav={onProductChange ? 'notes' : undefined} dir='rtl' sx={{padding: 0, width: 30}} align='center'>
                {!onProductChange ? (
                    <>
                        <Typography variant='h5'>      {row.notes ?? ''}</Typography>
                    </>
                ) : <Input
                    type='text'
                    onChange={(v) => {
                        onProductChange({...row, notes: v.target.value});
                    }}
                    value={row.notes}
                />}

            </TableCell>
            : undefined
        }
    </TableRow>

})

const CustomerProductsPreviewList = ({
                                         products,
                                         onProductChange,
                                         disableHoverableIncrementer = false,
                                         withReturns = true,
                                         date = new Date(),
                                         id,
                                         sup = false,
                                         displayCost = false,
                                         oldProducts,
                                         displayOldProducts,
                                         orderProducts,
                                         displayOrderProducts,
                                         collect,
     jumpToWrapped,
     enterMovesDown,
     sortByPosition,
     onTogglePositionSort,
                                     }: CustomerProductsPreviewListProps) => {
    const theme = useTheme();
    const cache = useQueryClient();
    const dispatch = useDispatch();
    const [onlyClientProduct, setOnlyClientProduct] = React.useState(!!sup)
    const hasValidClientId = !!id && Number(id) > 0;
    const [allProducts, setAllProducts] = React.useState(() => {
        if (sup) {
            const supplier = JSDB().getSupplier(Number(id))?.first;
            return toArrayOrEmpty(onlyClientProduct
                ? supplier?.getActiveProducts?.()
                : JSDB().getSpecificSupplierProducts(id));
        }
        if (!hasValidClientId) {
            return toArrayOrEmpty(JSDB().getAllClientProduct(true).first);
        }
        const client = JSDB().getClient(Number(id))?.first;
        return toArrayOrEmpty(onlyClientProduct ? client?.getActiveProducts?.() : JSDB().getAllClientProduct(true).first);
    });

    const pricesQueryKey = sup ? SUPPLIER_PRODUCTS : CUSTOMER_PRODUCTS;
    const {
        data: prices,
        isLoading,
        refetch
    } = useQuery(
        [pricesQueryKey, id, dateObjectToString(date)],
        () => (sup
            ? getSupplierProductsPrices(id, dateObjectToString(date), true)
            : getCustomerProductsPrices(id, dateObjectToString(date), true, undefined, true, true)
        ).catch(() => {
            dispatch({type: SNACKBAR_OPEN, open: true, message: 'שגיאה בגישה לנתונים', variant: 'alert', alertSeverity: 'error'});
            return [];
        }),
        {
            refetchOnReconnect: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: hasValidClientId,
        }
    );
    React.useEffect(() => {
        if (!hasValidClientId) return;
        cache.removeQueries(pricesQueryKey);
        refetch();
    }, [dispatch, sup, hasValidClientId]);
    React.useEffect(() => {
        if (!isLoading) {
            const fromApi = prices?.filter((p) => (!onlyClientProduct || p.available && p.totalPrice > 0)) ?? [];
            if (fromApi.length > 0 || !sup) {
                setAllProducts(fromApi);
            } else {
                const supplier = JSDB().getSupplier(Number(id))?.first;
                setAllProducts(toArrayOrEmpty(onlyClientProduct
                    ? supplier?.getActiveProducts?.()
                    : JSDB().getSpecificSupplierProducts(id)));
            }
        }
    }, [prices, onlyClientProduct, sup, isLoading, id]);

    const [multiSelectProducts, setMultiSelectProducts] = React.useState(false);
    const [addProductOpen, setAddProductOpen] = React.useState(false);
    const [addProductInput, setAddProductInput] = React.useState('');
    const addProductInputRef = useRef<HTMLInputElement | null>(null);
    const addProductSearchRef = useRef('');
    const selRef = useRef<HTMLTableSectionElement | null>(null);
    const pendingFocusRef = useRef<number | null>(null);
    const productsRef = useRef(products);
    productsRef.current = products;
    const taklitLookupRef = useRef<Map<number, TaklitProduct>>(new Map());
    const taklitReadyRef = useRef(false);
    const useTaklitOnAdd = !sup && isTaklitEnabled();
    React.useEffect(() => {
        if (!useTaklitOnAdd || !hasValidClientId) {
            taklitLookupRef.current = new Map();
            taklitReadyRef.current = false;
            return;
        }
        let cancelled = false;
        taklitReadyRef.current = false;
        loadTaklitLookup(date).then((map) => {
            if (!cancelled) {
                taklitLookupRef.current = map;
                taklitReadyRef.current = true;
            }
        });
        return () => { cancelled = true; };
    }, [useTaklitOnAdd, hasValidClientId, date]);
    const [externalIdSearch, setExternalIdSearch] = React.useState(parseInt(localStorage.getItem('externalIdOrders') ?? '0') == 1);
    // covers products with a second unit, leftover wrapped_amount after the unit was removed, and/or an order-only third unit
    const withExtraUnit = products.some((p) => showsWrappedColumn(p))
    const variableConversion = isVariableConversionRate();
    const notePlacementCounter = useRef(products.length > 0 ? Math.max(...products.map(p => p.notePlacement ?? 0)) + 1 : 0);
    React.useEffect(() => {
        notePlacementCounter.current =
            products.length > 0 ? Math.max(...products.map((p) => p.notePlacement ?? 0)) + 1 : 0;
    }, [products]);
    const duplicateProductLines = (source: IItem, count: number) => {
        if (!onProductChange) return;
        const n = Math.min(200, Math.max(1, Math.floor(count)));
        if (!Number.isFinite(n)) return;
        for (let k = 0; k < n; k++) {
            const notePlacement = notePlacementCounter.current;
            notePlacementCounter.current += 1;
            onProductChange({
                ...source,
                forceShow: true,
                notePlacement,
            });
        }
    };
    useLayoutEffect(() => {
        // Focus the line that was just picked, before the next key is typed.
        // Column index used to point at whatever row happened to be last, and a
        // late effect let that key change a different number.
        const placement = pendingFocusRef.current;
        if (placement == null || multiSelectProducts || !selRef.current) return;
        const row = selRef.current.querySelector(`tr[data-line="${placement}"]`);
        if (!row) return;
        const search = addProductInputRef.current;
        if (search && document.activeElement === search && search.value.trim() !== '') {
            pendingFocusRef.current = null;
            return;
        }
        const preferExtra = !!jumpToWrapped && row.getAttribute('data-extra') === '1';
        const input = (preferExtra
            ? row.querySelector('[data-nav="qty2"] input, [data-nav="qty3"] input, [data-nav="qty"] input')
            : row.querySelector('[data-nav="qty"] input')) as HTMLInputElement | null;
        if (!input) return;
        pendingFocusRef.current = null;
        input.focus();
        input.select();
    }, [products, jumpToWrapped, multiSelectProducts]);
    const unitSum = roundQty(products.reduce((sum, x) => sum + ((x.in_unit && x.in_unit > 0) ? x.quantity * (x.in_unit ?? 0) : 0), 0));
    const costSum = roundDecimals(products.reduce((sum, x) => sum + (x.costPrice ? x.costPrice : 0) * roundQty(chargedPositiveQuantity(x)), 0));
    const displayNoteColumn = products.some((p) => p.notes)
    const displayQuantityColumn = products.some((p) => p.quantityReturns)
    const displayCostPrice = products.some((p) => p.costPrice)
    const [existingOldProducts, setexistingOldProducts] = React.useState([])
    const [removedProducts, setremovedProducts] = React.useState([])
    const [existingOrderProducts, setexistingOrderProducts] = React.useState([])
    const [notExistingOrderProducts, setnotExistingOrderProducts] = React.useState([])
    const [withDiscount, setWithDiscount] =React.useState<boolean>(parseInt(localStorage.getItem('withDiscount') ?? '0') == 1);
    React.useEffect(() => {
        if (displayOldProducts) {
            const existingOldProductsTemp = []
            const removedProductsTemp = []
            oldProducts?.forEach((p) => {
                if (products.some((p2) => p2.id == p.id)) {
                    existingOldProductsTemp.push(p)
                } else {
                    removedProductsTemp.push(p)
                }
            });
            setexistingOldProducts(existingOldProductsTemp)
            setremovedProducts(removedProductsTemp)
        } else {
            setexistingOldProducts([])
            setremovedProducts([])
        }

    }, [displayOldProducts]);
    React.useEffect(() => {
        if (displayOrderProducts) {
            const existingOrderProductsTemp = []
            const notExistingOrderProductsTemp = []

            displayOrderProducts && orderProducts?.forEach((p) => {
                if (products.some((p2) => p2.id == p.id)) {
                    existingOrderProductsTemp.push(p)
                } else {
                    notExistingOrderProductsTemp.push(p)
                }
            });
            setexistingOrderProducts(existingOrderProductsTemp)
            setnotExistingOrderProducts(notExistingOrderProductsTemp)
        } else {
            setexistingOrderProducts([])
            setnotExistingOrderProducts([])
        }

    }, [displayOrderProducts]);


    // Numeric columns share the free width evenly (the inputs themselves are compact),
    // so neither edit nor read-only mode ends up with the numbers shoved into a corner.
    const numericHeaderSx = {px: 0.5, whiteSpace: 'nowrap'} as const;

    return (
        <TableContainer component={Paper}>
            <Table size='small' aria-label='a dense table'>
                <TableHead>
                    <TableRow style={{backgroundColor: theme.palette.primary.light}}>
                        <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='left'>
                            {onTogglePositionSort ? (
                                <Chip
                                    label={<FormattedMessage id='Product'/>}
                                    size='small'
                                    color={sortByPosition ? 'primary' : 'default'}
                                    variant={sortByPosition ? 'filled' : 'outlined'}
                                    onClick={onTogglePositionSort}
                                    sx={{cursor: 'pointer', fontWeight: 'bold'}}
                                />
                            ) : (
                                <Typography variant='h5' fontWeight='bold'>
                                    <FormattedMessage id='Product'/>
                                </Typography>
                            )}
                        </TableCell>
                        {displayOrderProducts ?
                            <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                                <Typography variant='h5' fontWeight='bold'>
                                    הזמנה
                                </Typography>
                            </TableCell>
                            : undefined
                        }

                        {withExtraUnit ? <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='left'>
                            <Typography variant='h5' fontWeight='bold'>
                                {variableConversion ? 'רוחב' : 'תיאור'}
                            </Typography>
                        </TableCell> : undefined}
                        {variableConversion && withExtraUnit ? <TableCell sx={numericHeaderSx} align='center'>
                            <Typography variant='h5' fontWeight='bold'>
                                אורך
                            </Typography>
                        </TableCell> : undefined}
                        <TableCell sx={numericHeaderSx} align='center'>
                            <Typography variant='h5' fontWeight='bold'>
                                כמות
                            </Typography>
                        </TableCell>

                        {withReturns && (displayQuantityColumn || onProductChange) ?
                            <TableCell sx={numericHeaderSx} align='center'>
                                <Typography variant='h5' fontWeight='bold'>
                                    חזרות
                                </Typography>
                            </TableCell> : undefined}
                        <TableCell sx={numericHeaderSx} align='center'>
                            <Typography variant='h5' fontWeight='bold'>
                                מחיר
                            </Typography>
                        </TableCell>
                        {withDiscount ? <TableCell sx={numericHeaderSx} align='center'>
                            <Typography variant='h5' fontWeight='bold'>
                                הנחה
                            </Typography>
                        </TableCell> : undefined}
                        <TableCell sx={numericHeaderSx} align='center'>
                            <Typography variant='h5' fontWeight='bold'>
                                {/* eslint-disable-next-line react/no-unescaped-entities */}
                                סה''כ
                            </Typography>
                        </TableCell>
                        {displayCost && displayCostPrice ?
                            <TableCell sx={numericHeaderSx} align='center'>
                                <Typography variant='h5' fontWeight='bold'>
                                    עלות(לפני מעמ)
                                </Typography>
                            </TableCell>
                            : undefined}
                        {
                            (displayNoteColumn || onProductChange) ?
                                <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                                    <Typography variant='h5' fontWeight='bold'>
                                        הערות
                                    </Typography>
                                </TableCell>
                                : undefined
                        }
                    </TableRow>


                </TableHead>
                <TableBody ref={selRef}>
                    {products.map((row, i) => (
                        <ProductRowMemo oldProducts={existingOldProducts} key={`p:${i}:${row.id}:${row.notePlacement ?? ''}`} displayNoteColumn={displayNoteColumn}
                                        displayQuantityColumn={displayQuantityColumn} row={row}
                                        onProductChange={onProductChange ? (product: IItem) => onProductChange(product, i) : undefined} i={i}
                                        disableHoverableIncrementer={disableHoverableIncrementer}
                                        displayCostPrice={displayCostPrice} displayCost={displayCost}
                                        withReturns={withReturns} withExtraUnit={withExtraUnit}
                                        existingOrderProducts={existingOrderProducts}
                                        externalIdSearch={externalIdSearch}
                                        withDiscount={withDiscount} jumpToWrapped={jumpToWrapped}
                                        enterMovesDown={enterMovesDown} variableConversion={variableConversion}
                                        onDuplicateLines={onProductChange ? duplicateProductLines : undefined}/>
                    ))}
                    {displayOldProducts && removedProducts?.map((row, i) => (
                        <ProductRowMemo removedProduct={true} key={`r:${i}:${row.id}:${row.notePlacement ?? ''}`} displayNoteColumn={displayNoteColumn}
                                        displayQuantityColumn={displayQuantityColumn} row={row} i={i}
                                        disableHoverableIncrementer={disableHoverableIncrementer}
                                        displayCostPrice={displayCostPrice} displayCost={displayCost}
                                        withReturns={withReturns} withExtraUnit={withExtraUnit}
                                        externalIdSearch={externalIdSearch}
                                        withDiscount={withDiscount} jumpToWrapped={jumpToWrapped}
                                        enterMovesDown={enterMovesDown} variableConversion={variableConversion}
                                        onDuplicateLines={onProductChange ? duplicateProductLines : undefined}/>
                    ))}
                    {displayOrderProducts && notExistingOrderProducts?.map((row, i) => (
                        <ProductRowMemo orderProducts={true} key={`o:${i}:${row.id}:${row.notePlacement ?? ''}`} displayNoteColumn={displayNoteColumn}
                                        displayQuantityColumn={displayQuantityColumn} row={row} i={i}
                                        disableHoverableIncrementer={disableHoverableIncrementer}
                                        displayCostPrice={displayCostPrice} displayCost={displayCost}
                                        withReturns={withReturns} withExtraUnit={withExtraUnit}
                                        externalIdSearch={externalIdSearch} jumpToWrapped={jumpToWrapped}
                                        withDiscount={withDiscount}
                                        enterMovesDown={enterMovesDown} variableConversion={variableConversion}
                                        onDuplicateLines={onProductChange ? duplicateProductLines : undefined}/>
                    ))}
                    {onProductChange ? <TableRow className={'autocomp'}>
                        <TableCell colSpan={6} sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                            <div style={{display: 'flex', alignItems: 'center', gap: 8, width: '100%'}}>
                                <Autocomplete
                                    sx={{flex: 1}}
                                    value={null}
                                    inputValue={addProductInput}
                                    onInputChange={(_event, value, reason) => {
                                        // Keep the family/group search after a pick in multi-select.
                                        if (multiSelectProducts && reason === 'reset') {
                                            setAddProductInput(addProductSearchRef.current);
                                            return;
                                        }
                                        addProductSearchRef.current = value;
                                        setAddProductInput(value);
                                    }}
                                    open={addProductOpen}
                                    onOpen={() => setAddProductOpen(true)}
                                    onClose={(_event, reason) => {
                                        if (multiSelectProducts && reason === 'selectOption') {
                                            return;
                                        }
                                        setAddProductOpen(false);
                                    }}
                                    blurOnSelect={!multiSelectProducts}
                                    clearOnBlur={!multiSelectProducts}
                                    disableCloseOnSelect={multiSelectProducts}
                                    ListboxProps={multiSelectProducts ? {
                                        onMouseDown: (event) => {
                                            event.preventDefault();
                                        }
                                    } : undefined}
                                    onChange={(_event, value) => {
                                            if (!value) return;
                                            let iItem = itemApiIInternal(sup ? JSDB().getSupplier(Number(id)).first : JSDB().getClient(Number(id)).first, dateObjectToString(date), value.id, sup);
                                            iItem.quantity = 0;
                                            iItem.forceShow = true;
                                            iItem.notePlacement = notePlacementCounter.current;
                                            notePlacementCounter.current += 1;
                                            if (useTaklitOnAdd && taklitReadyRef.current) {
                                                iItem = applyTaklitPriceFromLookup(iItem, taklitLookupRef.current, {markTouched: true}).item;
                                            }
                                            const added = iItem;
                                            onProductChange(added);
                                            if (multiSelectProducts) {
                                                setAddProductOpen(true);
                                                requestAnimationFrame(() => {
                                                    addProductInputRef.current?.focus();
                                                });
                                            } else {
                                                pendingFocusRef.current = added.notePlacement ?? null;
                                                setAddProductInput('');
                                            }
                                            // Price can arrive after the line is on screen. Merge into the
                                            // current line so a quantity typed in the meantime is kept.
                                            if (useTaklitOnAdd && !taklitReadyRef.current) {
                                                const placement = added.notePlacement;
                                                const productId = added.id;
                                                loadTaklitLookup(date).then((lookup) => {
                                                    taklitLookupRef.current = lookup;
                                                    taklitReadyRef.current = true;
                                                    const live = productsRef.current.find((p) => p.id === productId && p.notePlacement === placement) ?? added;
                                                    const priced = applyTaklitPriceFromLookup(live, lookup, {markTouched: true});
                                                    if (!priced.applied || priced.item.totalPrice === live.totalPrice) return;
                                                    onProductChange(priced.item);
                                                });
                                            }
                                        }
                                    }
                                    loading={isLoading}
                                    options={allProducts}
                                    getOptionLabel={(o) => `${externalIdSearch || o.external_id != '-1' && o.external_id != '' ? o.external_id : o.id}  - ${o.name}`}
                                    filterOptions={(options, {inputValue}) => {
                                        return options.filter(o => {
                                                return subStringMatch(inputValue.toLowerCase(), [externalIdSearch || o.external_id != '-1' && o.external_id != '' ? o.external_id : o.id, o.name.toLowerCase()])
                                            }
                                        ).sort((a, b) => {
                                            if (inputValue == '') {
                                                return a.position > b.position ? 1 : -1
                                            } else {
                                                const n1 = (externalIdSearch || a.external_id != '-1' && a.external_id != '' ? a.external_id : a.id)
                                                const n2 = (externalIdSearch || b.external_id != '-1' && b.external_id != '' ? b.external_id : b.id)
                                                const nn1 = Number(n1)
                                                const nn2 = Number(n2)
                                                if (!isNaN(nn1) && !isNaN(nn2)) {
                                                    return nn1 >= nn2 ? 1 : -1
                                                } else if (!isNaN(nn1)){
                                                    return 1
                                                } else if (!isNaN(nn2)){
                                                    return -1
                                                }
                                                else
                                                    return n1 > n2 ? 1 : -1
                                            }
                                        })
                                    }}
                                    size='small'
                                    fullWidth
                                    autoHighlight
                                    renderOption={(props, option) => {
                                        const pickedCount = products.filter(p => p.id === option.id).length;
                                        return (
                                            <li {...props} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                                <span>{`${externalIdSearch || option.external_id != '-1' && option.external_id != '' ? option.external_id : option.id}  - ${option.name}`}</span>
                                                {pickedCount > 0 && (
                                                    <span style={{display: 'flex', alignItems: 'center', marginLeft: 8}}>
                                                        <CheckCircleIcon color='success' fontSize='small' />
                                                        <span style={{fontSize: 12, marginLeft: 2}}>{pickedCount > 1 ? `x${pickedCount}` : ''}</span>
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={'הוסף מוצר'}
                                            fullWidth
                                            size='small'
                                            inputRef={addProductInputRef}
                                            inputProps={{
                                                ...params.inputProps,
                                                onKeyDown: (event) => {
                                                    // List is closed: ArrowUp returns to the quantity above.
                                                    // While it is open, arrows stay on the suggestion list.
                                                    if (!addProductOpen && event.key === 'ArrowUp') {
                                                        let cursor = (event.currentTarget as HTMLElement).closest('tr')?.previousElementSibling ?? null;
                                                        while (cursor) {
                                                            const input = cursor.querySelector('[data-nav="qty"] input') as HTMLInputElement | null;
                                                            if (input) {
                                                                (event as any).defaultMuiPrevented = true;
                                                                event.preventDefault();
                                                                input.focus();
                                                                input.select();
                                                                return;
                                                            }
                                                            cursor = cursor.previousElementSibling;
                                                        }
                                                    }
                                                    params.inputProps.onKeyDown?.(event);
                                                },
                                            }}
                                        />
                                    )}
                                />
                                <Chip
                                    size="small"
                                    color={multiSelectProducts ? 'primary' : 'default'}
                                    variant={multiSelectProducts ? 'filled' : 'outlined'}
                                    label={
                                        <FormControlLabel
                                            sx={{m: 0, '& .MuiFormControlLabel-label': {fontSize: 12}}}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={multiSelectProducts}
                                                    onChange={(e) => {
                                                        setMultiSelectProducts(e.target.checked);
                                                        if (e.target.checked) {
                                                            setAddProductOpen(true);
                                                            requestAnimationFrame(() => {
                                                                addProductInputRef.current?.focus();
                                                            });
                                                        } else {
                                                            setAddProductOpen(false);
                                                        }
                                                    }}
                                                    sx={{p: 0.25}}
                                                />
                                            }
                                            label="בחירה מרובה"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    sx={{flexShrink: 0}}
                                />
                            </div>
                        </TableCell>
                        <TableCell colSpan={1} sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                            <FormControlLabel
                                value='low'
                                control={
                                    <Checkbox checked={onlyClientProduct} onChange={(e) => setOnlyClientProduct(e.target.checked)}/>
                                }
                                label='זמינים'
                            />


                        </TableCell>
                    </TableRow> : undefined}
                    {products.length > 1 ? <TableRow style={{backgroundColor: '#F3F3F3'}}>
                        <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='left'>
                            <Typography variant='h5' fontWeight='bold'>
                                סיכום
                            </Typography>
                        </TableCell>
                        {
                            displayOrderProducts ?
                                <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                                    <Typography variant='h5' fontWeight='bold'>
                                        {roundQty(existingOrderProducts.reduce((sum, x) => sum + x.quantity + x.quantitySecond, 0))}
                                    </Typography>
                                </TableCell>
                                : undefined
                        }
                        {withExtraUnit ? <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='left'>
                            <Typography variant='h5' fontWeight='bold'>
                                {roundQty(products.reduce((sum, x) => sum + x.quantitySecond, 0))}
                            </Typography>
                            {/* order-only third unit (no conversion): summed separately, never mixed into the second-unit sum above */}
                            {products.some((x) => hasThirdUnit(x) || (x.quantityThird ?? 0) !== 0) ? (
                                <Typography variant='h5' fontWeight='bold'>
                                    {roundQty(products.reduce((sum, x) => sum + (x.quantityThird ?? 0), 0))}
                                </Typography>
                            ) : undefined}
                        </TableCell> : undefined}
                        {variableConversion && withExtraUnit ? <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'/> : undefined}

                        <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>

                            <Typography variant='h5' fontWeight='bold'>
                                {roundQty(products.reduce((sum, x) => sum + chargedPositiveQuantity(x), 0))}
                            </Typography>

                            {unitSum > 0 ? <Typography variant='h5'>({unitSum}) </Typography> : undefined}


                        </TableCell>
                        {withReturns && (displayQuantityColumn || onProductChange) ? <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                            <Typography variant='h5' fontWeight='bold'>
                                {roundQty(products.reduce((sum, x) => sum + x.quantityReturns, 0))}
                            </Typography>
                        </TableCell> : undefined}


                        <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                            <Typography variant='h5' fontWeight='bold'>

                            </Typography>
                        </TableCell>
                        {withDiscount ? <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                            <Typography variant='h5' fontWeight='bold'>

                            </Typography>
                        </TableCell> : undefined}
                        <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                            <Typography variant='h5' fontWeight='bold'>
                                {/* eslint-disable-next-line react/no-unescaped-entities */}

                            </Typography>
                        </TableCell>
                        {displayCost && displayCostPrice ?
                            <TableCell dir='ltr' sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                                <Typography variant='h5' fontWeight='bold'>
                                    {costSum.toLocaleString()}
                                    <IconCurrencyShekel size={10} color='black'/>
                                </Typography>
                            </TableCell>
                            : undefined}
                        {(displayNoteColumn || onProductChange) ?
                            <TableCell sx={{paddingRight: 0, paddingLeft: 0}} align='center'>
                                <Typography variant='h5' fontWeight='bold'>

                                </Typography>
                            </TableCell>
                            : undefined}
                    </TableRow> : undefined}

                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default memo(CustomerProductsPreviewList);
